import styled from 'styled-components'
import { observer } from 'mobx-react'

import { theme } from '../../styles/theme'
import { PrimaryButton } from '../Button/Button'
import { SvgIcons } from '../Icon/Icon'
import { SpinnerIcon } from '../Spinner/Spinner'
import { BodyText } from '../Typography/Typography'

import type { ErrorModel, OperationStatus } from '../../models/common/OperationStatus'
import type { Instance } from 'mobx-state-tree'

const ErrorMessageContainer = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2em;
`

interface FetchErrorMessageProps {
  error: Instance<typeof ErrorModel>
  errorMessagePrefix: string
  onRetry?: () => Promise<void>
}

const FetchErrorMessage = ({ error, errorMessagePrefix, onRetry }: FetchErrorMessageProps) => {
  return (
    <ErrorMessageContainer>
      <SvgIcons.ErrorSvg />
      <BodyText>
        {errorMessagePrefix}: {error.message}{' '}
      </BodyText>
      {onRetry !== undefined && <PrimaryButton text="Try Again" onClick={() => { void onRetry() }} />}
    </ErrorMessageContainer>
  )
}

interface PageContentOrErrorOrSpinnerProps
  extends React.PropsWithChildren<{
    fetchStatus: OperationStatus
    inProgressTitle: string
    errorMessagePrefix: string
    onRetry?: () => Promise<void>
  }> {}

const ErrorOrSpinnerContainer = styled.div`
  block-size: 100%;
  inline-size: 100%;
  background-color: ${theme.colors.White};
  border: solid 1px ${theme.colors.MediumGrey};
  display: inline-flex;
  overflow: auto;
`

export const PageContentOrErrorOrSpinner = observer(
  ({ fetchStatus, inProgressTitle, errorMessagePrefix, children, onRetry }: PageContentOrErrorOrSpinnerProps) => {
    if (fetchStatus.isSuccess) {
      return <>{children}</>
    }

    const onRetryObj = onRetry ? { onRetry } : {}
    return (
      <ErrorOrSpinnerContainer>
        {fetchStatus.inProgress ? (
          <SpinnerIcon {...{ title: inProgressTitle }} />
        ) : fetchStatus.error ? (
          <FetchErrorMessage {...{ error: fetchStatus.error, errorMessagePrefix, ...onRetryObj }} />
        ) : null}
      </ErrorOrSpinnerContainer>
    )
  },
)
